import React, { useEffect, useState, useCallback } from "react";
import LoadingSpinner from "../../../HelperComponents/LoadingSpinner";
import MessageTile from "../../../HelperComponents/MessageTile";
import { deleteListItem, getEducationHistory } from "../../../Service/Services";
import { formatPeriod } from "../../../Utility/Utility";
import AddEditEducationHistory from "./AddEditEducationHistory";
import ConfirmDialog from "../../../HelperComponents/ConfirmDialog";
import IconCircleButton from "../../../HelperComponents/IconCircleButton";

interface EducationHistoryProps {
  user: any;
}

type EducationItem = {
  ID: number;
  School?: string;
  UserDegree?: string;
  FieldsOfStudy?: string | null;
  WorkAddress?: string | null;
  AttendedMonth?: string | null;
  AttendedYear?: string | null;
  UntilMonth?: string | null;
  UntilYear?: string | null;
  Graduated?: boolean;
};

const EducationHistory = (props: EducationHistoryProps) => {
  const [loading, setLoading] = useState(false);
  const [educationHistory, setEducationHistory] = useState<EducationItem[]>([]);
  const [editEduOpen, setEditEduOpen] = useState(false);
  const [selectedEducation, setSelectedEducation] =
    useState<EducationItem | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<
    "info" | "success" | "error" | "warning"
  >("info");

  const styles = {
    container: {
      fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif",
      padding: "12px",
    },
    toolbar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      margin: "0 0 12px 0",
    },
    toolbarTitle: {
      fontSize: "18px",
      fontWeight: 700,
      color: "hsl(var(--primary))",
    },
    grid: {
      width: "100%",
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
      gridGap: "15px",
      alignItems: "start",
    },
    card: {
      backgroundColor: `hsl(var(--background))`,
      borderRadius: "12px",
      boxShadow: "0 2px 12px rgba(0, 0, 0, 0.07)",
      padding: "18px",
      border: "1px solid hsl(var(--border))",
    },
    sectionTitle: {
      fontSize: "16px",
      fontWeight: "700",
      color: "hsl(var(--primary))",
      paddingBottom: "8px",
      borderBottom: "1px solid hsl(var(--border))",
      marginBottom: "12px",
    },
    detailRow: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      marginBottom: "10px",
    },
    label: {
      fontSize: "13px",
      fontWeight: "600",
      color: "hsl(var(--foreground))",
    },
    value: {
      fontSize: "13px",
      color: "hsl(var(--foreground))",
      textAlign: "left" as const,
    },
    active: {
      color: "hsl(var(--success))",
      fontWeight: "500",
    },
    inactive: {
      color: "hsl(var(--error))",
      fontWeight: "500",
    },
    headerRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "8px",
    },
    titleGroup: {
      display: "inline-flex",
      alignItems: "center",
      gap: "40px",
    },
    cardActions: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    },
    badge: {
      display: "inline-block",
      fontSize: "12px",
      fontWeight: 600,
      padding: "4px 8px",
      borderRadius: "999px",
      backgroundColor: "hsl(var(--primary))",
      color: "#FFF",
      border: "1px solid hsl(var(--border))",
    },

    iconButton: {
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      background: "hsl(var(--primary))",
      color: "hsl(var(--foreground))",
      border: "1px solid hsl(var(--border))",
      borderRadius: "8px",
      padding: "4px 8px",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: 600,
    },
  };

  const fetchEducationHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getEducationHistory(props.user?.FullName);
      // const response = await getEducationHistory("cassie malone");
      setEducationHistory(response.data.items as EducationItem[]);
    } catch (error) {
      console.error("Error fetching education history:", error);
    } finally {
      setLoading(false);
    }
  }, [props?.user?.FullName]);

  const handleDelete = async (education: EducationItem) => {
    try {
      const res = await deleteListItem(
        education.ID,
        "Schools and Education",
        props.user.Email,
      );
      setDeleteOpen(false);
      if (res.status === 204) {
        setMessageType("success");
        setMessage("Education record deleted successfully.");
        await fetchEducationHistory();
        setSelectedEducation(null);
        setEditEduOpen(false);
        return;
      }

      setMessageType("error");
      setMessage("Delete failed. Please try again.");
    } catch (err: any) {
      setMessageType("error");
      setMessage(err?.message || "Error deleting record. Please try again.");
    }
  };

  useEffect(() => {
    if (!props.user) return;
    fetchEducationHistory();
  }, [props.user, fetchEducationHistory]);

  return (
    <div className="editProfile-container">
      {message && (
        <MessageTile
          isVisible={!!message}
          message={message}
          type={messageType}
        />
      )}
      {loading && <LoadingSpinner />}
      {!loading && educationHistory.length === 0 && (
        <div
          style={{
            padding: "16px",
            color: "hsl(var(--foreground))",
            background: "hsl(var(--background))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "8px",
          }}
        >
          No Education History Found.
        </div>
      )}
      <div className="editProfile-grid">
        {educationHistory.map((item) => (
          <div key={item.ID} style={styles.card}>
            <div style={styles.headerRow}>
              <div style={styles.titleGroup}>
                <h3 style={styles.sectionTitle}>{item.School}</h3>
                {item.WorkAddress && (
                  <span style={styles.badge}>{item.WorkAddress}</span>
                )}
              </div>
              <div style={styles.cardActions}>
                <IconCircleButton
                  label="Edit Education"
                  icon="mdi:pencil"
                  onClick={() => {
                    setSelectedEducation(item);
                    setEditEduOpen(true);
                  }}
                />
                <IconCircleButton
                  label="Delete Education"
                  icon="mdi:delete"
                  variant="delete"
                  onClick={() => {
                    setSelectedEducation(item);
                    setDeleteOpen(true);
                  }}
                />
              </div>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.label}>Degree</span>
              <span style={styles.value}>{item.UserDegree}</span>
            </div>
            {item.FieldsOfStudy && (
              <div style={styles.detailRow}>
                <span style={styles.label}>Field of Study</span>
                <span style={styles.value}>{item.FieldsOfStudy}</span>
              </div>
            )}
            <div style={styles.detailRow}>
              <span style={styles.label}>Period</span>
              <span style={styles.value}>
                {formatPeriod(
                  item.AttendedMonth,
                  item.AttendedYear,
                  item.UntilMonth,
                  item.UntilYear,
                )}
              </span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.label}>Graduated</span>
              <span style={styles.value}>
                {item.Graduated ? (
                  <span className="pillGreen">Graduated</span>
                ) : (
                  <span className="pillRed">Not Graduated</span>
                )}
              </span>
            </div>
          </div>
        ))}
        {/* Edit Education Modal */}
        {editEduOpen && selectedEducation && (
          <AddEditEducationHistory
            isOpen={editEduOpen}
            onDismiss={() => {
              setEditEduOpen(false);
              setSelectedEducation(selectedEducation);
            }}
            mode="update"
            itemId={selectedEducation?.ID}
            educationHistory={selectedEducation}
          />
        )}
        {deleteOpen && selectedEducation && (
          <ConfirmDialog
            isOpen={deleteOpen}
            title="Delete Education"
            message={`Are you sure you want to delete this education record? This action cannot be undone.`}
            confirmLabel="Delete"
            cancelLabel="Cancel"
            onConfirm={async () => {
              await handleDelete(selectedEducation);
              setDeleteOpen(false);
            }}
            onCancel={() => {
              setDeleteOpen(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default EducationHistory;
